import { useCallback, useEffect, useRef, useState, type UIEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChevronIcon from "../../../../../../../assets/svg/chevron-icon";
import CheckIcon from "../../../../../../../assets/svg/check-icon";
import CompletedIcon from "../../../../../../../assets/svg/completed-icon";
import PlusFilledIcon from "../../../../../../../assets/svg/plus-filled-icon";
import { visibleScrollbar } from "../../../../../../../utils/functions/get-visible-scrollbar";
import { TruncatedTextTooltip } from "../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import PulseLoader from "../../../../../../../components/Loader/PulseLoader";
import { HelpdeskViewItem } from "../utils/types";
import { getAllTicketsView } from "../utils/constant";
import { setDrawer } from "../../../../../../../redux/slices/drawerSlice";
import type { RootState } from "../../../../../../../redux/store";

interface ViewSelectorDropdownProps {
  selectedViewId?: string;
  setSelectedViewId?: React.Dispatch<React.SetStateAction<string>>;
  hasMoreViews?: boolean;
  isLoadingMoreViews?: boolean;
  onLoadMoreViews?: () => void | Promise<void>;
}

export const ViewSelectorDropdown = ({
  selectedViewId = "all_tickets",
  setSelectedViewId,
  hasMoreViews = false,
  isLoadingMoreViews = false,
  onLoadMoreViews,
}: ViewSelectorDropdownProps) => {
  const dispatch = useDispatch();

  const helpdeskViewsFromStore = useSelector(
    (state: RootState) => state.helpdesk.helpdeskViews
  );
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );

  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const helpdeskViews = helpdeskViewsFromStore ?? [];
  const allTicketsView = getAllTicketsView(region);

  const allViews: HelpdeskViewItem[] = [allTicketsView, ...helpdeskViews];
  const primarySelected: HelpdeskViewItem =
    allViews?.find((v) => v?.id === selectedViewId) ?? allTicketsView;

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelectView = useCallback(
    (viewId: string) => {
      setSelectedViewId?.(viewId);
    },
    [setSelectedViewId]
  );

  const handleCreatedViewsScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const element = event.currentTarget;
      if (
        element.scrollHeight - element.scrollTop - element.clientHeight >
        40
      ) {
        return;
      }
      if (!hasMoreViews || isLoadingMoreViews) return;
      void onLoadMoreViews?.();
    },
    [hasMoreViews, isLoadingMoreViews, onLoadMoreViews]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef?.current &&
        !dropdownRef?.current?.contains(event?.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="min-w-[180px] flex items-center justify-between gap-[4px] text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap border hover:bg-hover-effect dark:hover:bg-hover-effect-dk border-border-gray dark:border-border-gray-dk rounded-[6px] px-[9px] py-[8px]"
      >
        <div className="flex items-center gap-[4px]">
          {primarySelected?.id === "all_tickets" ? (
            <CompletedIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
          ) : (
            primarySelected?.emoji_icon && (
              <span className="text-2xl leading-none shrink-0">
                {primarySelected.emoji_icon}
              </span>
            )
          )}
          <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
            {primarySelected?.name}
          </span>
        </div>
        <ChevronIcon
          className={`w-[18px] h-[18px] shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-10 right-0 z-50 bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[20px] border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect p-3 flex flex-col gap-[18px]">
          <div className="flex gap-3">
            <div className="flex flex-col gap-3 min-w-[160px]">
              <span className="text-sm font-semibold text-gray-placeholder dark:text-gray-placeholder-dk uppercase tracking-wide">
                Saved Views
              </span>
              <div className="flex flex-col gap-[6px]">
                {(() => {
                  const isSelected = selectedViewId === "all_tickets";
                  return (
                    <button
                      onClick={() => handleSelectView("all_tickets")}
                      className={`flex items-center justify-between w-full py-[5px] px-2 rounded-[8px] cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk border border-selected-option-highlight dark:border-selected-option-highlight-dk"
                          : "border border-transparent hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <CompletedIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
                        <span
                          className={`text-md whitespace-nowrap ${
                            isSelected
                              ? "font-semibold text-verba-blue dark:text-verba-blue-dk"
                              : "font-normal text-verba-indigo dark:text-verba-indigo-dk"
                          }`}
                        >
                          {allTicketsView.name}
                        </span>
                      </div>
                      {isSelected && (
                        <CheckIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
                      )}
                    </button>
                  );
                })()}
              </div>
            </div>

            <div className="w-px bg-border-gray dark:bg-border-gray-dk self-stretch" />

            <div className="flex flex-col gap-3 min-w-[186px]">
              <span className="text-sm font-semibold text-gray-placeholder dark:text-gray-placeholder-dk uppercase tracking-wide">
                Created
              </span>
              <div
                onScroll={handleCreatedViewsScroll}
                className={`flex flex-col gap-[6px] max-h-[200px] overflow-y-auto overflow-x-hidden ${visibleScrollbar(
                  "[&::-webkit-scrollbar]:w-[8px]"
                )}`}
              >
                {helpdeskViews.map((apiView) => {
                  const isSelected = selectedViewId === apiView?.id;
                  return (
                    <button
                      key={apiView?.id}
                      onClick={() => handleSelectView(apiView?.id)}
                      className={`flex items-center justify-between w-full py-[5px] px-2 rounded-[8px] cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk border border-selected-option-highlight dark:border-selected-option-highlight-dk"
                          : "border border-transparent hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                      }`}
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        {apiView?.emoji_icon && (
                          <span className="text-2xl leading-none shrink-0">
                            {apiView.emoji_icon}
                          </span>
                        )}
                        <TruncatedTextTooltip
                          text={apiView?.name}
                          maxWidth="max-w-[150px]"
                          className={`text-md ${
                            isSelected
                              ? "font-semibold text-verba-blue dark:text-verba-blue-dk"
                              : "font-normal text-verba-indigo dark:text-verba-indigo-dk"
                          }`}
                        />
                      </div>
                      {isSelected && (
                        <CheckIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
                      )}
                    </button>
                  );
                })}
                {isLoadingMoreViews && (
                  <div className="flex justify-center py-2">
                    <PulseLoader size={6} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full h-px bg-border-gray dark:bg-border-gray-dk" />

          <button
            className="flex items-center gap-1 cursor-pointer group"
            onClick={() => {
              dispatch(setDrawer({ open: true, type: "helpdesk_view" }));
            }}
          >
            <PlusFilledIcon className="w-[18px] h-[18px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
            <span className="text-md font-medium text-verba-blue dark:text-verba-blue-dk group-hover:underline">
              Create New View
            </span>
          </button>
        </div>
      )}
    </div>
  );
};
